/**
 * Operations Calendar API Functions
 *
 * API functions for fetching and managing calendar events.
 */

import { api } from '@/lib/api';
import type {
  CalendarEvent,
  CalendarEventsParams,
  V4CalendarEventsResponse,
  V4RescheduleResponse,
  RescheduleInput,
} from './types';

// =============================================================================
// Transform Functions
// =============================================================================

/**
 * Transform V4 calendar event to internal format
 * Note: The backend already formats events for FullCalendar, so minimal transform needed
 */
function transformV4CalendarEvent(v4: CalendarEvent): CalendarEvent {
  // The backend already returns events in FullCalendar-compatible format
  // Just ensure types are correct
  return {
    ...v4,
    extendedProps: v4.extendedProps || {},
  };
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Fetch calendar events within a date range
 */
export async function fetchCalendarEvents(params: CalendarEventsParams): Promise<{
  events: CalendarEvent[];
  total: number;
}> {
  // Build query params - only include defined values
  const queryParams: Record<string, string | boolean> = {
    start: params.start,
    end: params.end,
  };

  if (params.types) {
    queryParams.types = params.types;
  }
  if (params.projectTypes) {
    queryParams.projectTypes = params.projectTypes;
  }
  if (params.assigneeIds) {
    queryParams.assigneeIds = params.assigneeIds;
  }
  if (params.includeCompleted) {
    queryParams.includeCompleted = true;
  }

  const data = await api.get<V4CalendarEventsResponse>(
    '/v4/operations-calendar/events',
    queryParams
  );

  return {
    events: data.events.map(transformV4CalendarEvent),
    total: data.total,
  };
}

/**
 * Reschedule a calendar event (project or task)
 */
export async function rescheduleEvent(input: RescheduleInput): Promise<V4RescheduleResponse> {
  const { id, type, newStart } = input;

  // Validate and extract the actual ID from the event ID (e.g., "project-uuid" -> "uuid")
  const match = id.match(/^(project|task|milestone)-(.+)$/);
  if (!match) {
    throw new Error(`Invalid event ID format: ${id}. Expected format: "type-uuid"`);
  }
  const actualId = match[2];

  // Validate date format (should be YYYY-MM-DD)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(newStart)) {
    throw new Error(`Invalid date format: ${newStart}. Expected format: YYYY-MM-DD`);
  }

  const data = await api.patch<V4RescheduleResponse>(
    `/v4/operations-calendar/events/${actualId}/reschedule`,
    {
      type,
      newStart,
    }
  );

  return data;
}
